import React from 'react';

import { Flex } from 'antd';

import BoxConfigHistoryTable from './BoxConfigHistoryTable';
import { useUpdateBoxConfiguration } from '../api/boxConfig/useUpdateBoxConfiguration';
import BoxConfigEdit from '../components/BoxConfigEdit';
import useBoxConfigHistory from '../hooks/useBoxConfigHistory';
import { useBoxConfiguration } from '../hooks/useBoxConfiguration';
import Loader from '@/components/Loader';
import useError from '@/hooks/useError';
import { usePagination } from '@/hooks/usePagination';
import useToasts from '@/shared/hooks/useToasts';
import { MessageType } from '@/shared/hooks/useToasts.types';

const BoxConfig = ({ boxId }: { boxId: string }) => {
  const { result, invalidate: fetchConfiguration } = useBoxConfiguration(
    boxId!
  );

  const addError = useError();
  const successMsg = useToasts((state) => state.addItem);

  const { mutateAsync: updateBoxConfiguration } = useUpdateBoxConfiguration({
    boxId: boxId!,
    config: {
      onSuccess: () => {
        fetchConfigHistory();
        fetchConfiguration();
        successMsg(
          { msg: 'Successfully updated config', type: MessageType.Success },
          'root',
          {}
        );
      },
      onError: (error) => {
        addError(error || 'Could not update box config', {
          trackError: false,
        });
      },
    },
  });

  const { params: paginationParams, setParams: setPaginationParams } =
    usePagination({
      page: 1,
      pageSize: 20,
      orderDirection: 'DESC',
      orderColumn: 'createdAt',
    });

  const { result: boxConfigHistory, invalidate: fetchConfigHistory } =
    useBoxConfigHistory(boxId!, paginationParams);

  if (result.state === 'idle' || result.state === 'loading') {
    return <Loader size={32} />;
  }

  if (result.state === 'error') return <>Error fetching configuration.</>;

  const { resource: boxConfiguration } = result;

  return (
    <Flex gap={32} align="start">
      <BoxConfigEdit
        initialValue={JSON.stringify(boxConfiguration, null, 2)}
        onSave={async (newBoxConfig) => {
          try {
            const parsedConfig = JSON.parse(newBoxConfig);

            // Validate that code and securityCode are not identical

            const codeAndSecurityCodeIdentical =
              parsedConfig?.opening?.code &&
              parsedConfig?.opening?.securityCode &&
              parsedConfig.opening.code !== '0000' &&
              parsedConfig.opening.securityCode !== '0000' &&
              parsedConfig.opening.code === parsedConfig.opening.securityCode;

            if (codeAndSecurityCodeIdentical) {
              addError('Code and Security Code cannot be identical', {
                trackError: false,
              });

              return;
            }

            await updateBoxConfiguration({ newData: parsedConfig });
          } catch (e) {
            addError('Invalid JSON format', {
              trackError: false,
            });
          }
        }}
      />
      <BoxConfigHistoryTable
        boxConfigHistory={boxConfigHistory}
        paginationParams={paginationParams}
        setPaginationParams={setPaginationParams}
      />
    </Flex>
  );
};

export default BoxConfig;
